import type { I18N } from '@vista-digital/i18n'
import type { Item, OrderItemLine, Site } from '@vista-digital/ocapi-types/v1'
import { getOrder, getOrderTotalPrice, type OrderItemLineSelection } from '@vista-digital/occ-domain'
import { gtm } from '../../gtm.service'
import { Ecommerce, EcommerceDataLayer } from '../../models-gtm'
import { getConcessionCartItems } from '../../utils/getConcessionCartItems'
import { getConcessionCompletedOrderData } from '../../utils/getConcessionCompletedOrderData'
import { getConcessionPaymentEcommerceModel } from '../../utils/getConcessionPaymentEcommerceModel'
import { getDedupedCartItems } from '../../utils/getDedupedCartItems'

export type ConcessionLine = OrderItemLineSelection | OrderItemLine | Item

export class FnbGtmEvents {
    public static selectItem(concessions: ConcessionLine[], site: Site, i18n: I18N) {
        const items = this.getConcessionEcommerceModelFromCartItem(concessions, site, i18n)
        gtm.sendDataLayer(new EcommerceDataLayer('select_item', items))
    }

    public static addToCart(concessions: ConcessionLine[], site: Site, i18n: I18N) {
        const items = this.getConcessionEcommerceModelFromCartItem(concessions, site, i18n)

        gtm.sendDataLayer(new EcommerceDataLayer('add_to_cart', items))
    }

    public static removeFromCart(concessions: ConcessionLine[], site: Site, i18n: I18N) {
        const items = this.getConcessionEcommerceModelFromCartItem(concessions, site, i18n)

        gtm.sendDataLayer(new EcommerceDataLayer('remove_from_cart', items))
    }

    public static beginCheckout(concessions: ConcessionLine[], site: Site, i18n: I18N) {
        const items = this.getConcessionEcommerceModelFromCartItem(concessions, site, i18n)

        gtm.sendDataLayer(new EcommerceDataLayer('begin_checkout', items))
    }

    public static addPaymentInfo(concessions: ConcessionLine[], site: Site, i18n: I18N) {
        const items = this.getConcessionEcommerceModelFromCartItem(concessions, site, i18n)

        gtm.sendDataLayer(new EcommerceDataLayer('add_payment_info', items))
    }

    public static async purchase(orderId: string, i18n: I18N) {
        const { items, site, value, transactionId, vistaTransactionId } = await getConcessionCompletedOrderData(orderId)

        if (!items || !site) return

        const ecommerceData = getConcessionPaymentEcommerceModel(
            items,
            site,
            i18n,
            value,
            transactionId,
            vistaTransactionId
        )

        gtm.sendDataLayer(new EcommerceDataLayer('purchase', ecommerceData))
    }

    private static getConcessionEcommerceModelFromCartItem(
        concessions: ConcessionLine[],
        site: Site,
        i18n: I18N
    ): Ecommerce {
        const items = getConcessionCartItems(concessions, site, i18n)
        const dedupedCartItems = getDedupedCartItems(items)
        return new Ecommerce(dedupedCartItems, getOrderTotalPrice().valueIncludingTax, getOrder()?.id)
    }
}
